import _extends from "@babel/runtime/helpers/extends";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx, keyframes } from '@emotion/react';
import { getStyleProps } from '../utils';

// ==============================
// Dropdown & Clear Icons
// ==============================

const styles = css({
  display: 'inline-block',
  fill: 'currentColor',
  lineHeight: 1,
  stroke: 'currentColor',
  strokeWidth: 0
});
const Svg = ({
  size,
  ...props
}) => jsx("svg", _extends({
  height: size,
  width: size,
  viewBox: "0 0 20 20",
  "aria-hidden": "true",
  focusable: "false",
  css: styles
  // eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
}, props));
// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const CrossIcon = props =>
// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
jsx(Svg, _extends({
  size: 20
}, props), jsx("path", {
  d: "M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
}));
// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const DownChevron = props =>
// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props
jsx(Svg, _extends({
  size: 20
}, props), jsx("path", {
  d: "M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
}));

// ==============================
// Dropdown & Clear Buttons
// ==============================

const baseCSS = ({
  isFocused,
  theme: {
    spacing: {
      baseUnit
    },
    colors
  }
}, unstyled) => ({
  label: 'indicatorContainer',
  display: 'flex',
  transition: 'color 150ms',
  ...(unstyled ? {} : {
    color: isFocused ? colors.neutral60 : colors.neutral20,
    padding: baseUnit * 2,
    ':hover': {
      color: isFocused ? colors.neutral80 : colors.neutral40
    }
  })
});
export const dropdownIndicatorCSS = baseCSS;

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const DropdownIndicator = props => {
  const {
    children,
    innerProps
  } = props;
  return jsx("div", _extends({}, getStyleProps(props, 'dropdownIndicator', {
    indicator: true,
    'dropdown-indicator': true
  }), innerProps), children || jsx(DownChevron, null));
};
export const clearIndicatorCSS = baseCSS;

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const ClearIndicator = props => {
  const {
    children,
    innerProps
  } = props;
  return jsx("div", _extends({}, getStyleProps(props, 'clearIndicator', {
    indicator: true,
    'clear-indicator': true
  }), innerProps), children || jsx(CrossIcon, null));
};

// ==============================
// Separator
// ==============================

export const indicatorSeparatorCSS = ({
  isDisabled,
  theme: {
    spacing: {
      baseUnit
    },
    colors
  }
}, unstyled) => ({
  label: 'indicatorSeparator',
  alignSelf: 'stretch',
  width: 1,
  ...(unstyled ? {} : {
    backgroundColor: isDisabled ? colors.neutral10 : colors.neutral20,
    marginBottom: baseUnit * 2,
    marginTop: baseUnit * 2
  })
});

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const IndicatorSeparator = props => {
  const {
    innerProps
  } = props;
  return jsx("span", _extends({}, innerProps, getStyleProps(props, 'indicatorSeparator', {
    'indicator-separator': true
  })));
};

// ==============================
// Loading
// ==============================

const loadingDotAnimations = keyframes({
  '0%, 80%, 100%': {
    opacity: 0
  },
  '40%': {
    opacity: 1
  }
});
export const loadingIndicatorCSS = ({
  isFocused,
  size,
  theme: {
    colors,
    spacing: {
      baseUnit
    }
  }
}, unstyled) => ({
  label: 'loadingIndicator',
  display: 'flex',
  transition: 'color 150ms',
  alignSelf: 'center',
  fontSize: size,
  lineHeight: 1,
  marginRight: size,
  textAlign: 'center',
  verticalAlign: 'middle',
  ...(unstyled ? {} : {
    color: isFocused ? colors.neutral60 : colors.neutral20,
    padding: baseUnit * 2
  })
});
const LoadingDot = ({
  delay,
  offset
}) => jsx("span", {
  // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
  css: {
    animation: `${loadingDotAnimations} 1s ease-in-out ${delay}ms infinite;`,
    backgroundColor: 'currentColor',
    borderRadius: '1em',
    display: 'inline-block',
    marginLeft: offset ? '1em' : undefined,
    height: '1em',
    verticalAlign: 'top',
    width: '1em'
  }
});
// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const LoadingIndicator = ({
  innerProps,
  isRtl,
  size = 4,
  ...restProps
}) => {
  return jsx("div", _extends({}, getStyleProps({
    ...restProps,
    innerProps,
    isRtl,
    size
  }, 'loadingIndicator', {
    indicator: true,
    'loading-indicator': true
  }), innerProps), jsx(LoadingDot, {
    delay: 0,
    offset: isRtl
  }), jsx(LoadingDot, {
    delay: 160,
    offset: true
  }), jsx(LoadingDot, {
    delay: 320,
    offset: !isRtl
  }));
};